import type { ReactElement } from "react";
import { Link } from "react-router-dom";
import { ArticleHero } from "../ArticleHero";
import familyHeroImage from "../../assets/images/family-fall-outfit-portrait.webp";
import family1 from "../../assets/images/mother-baby-spring-blossom-family-portrait.webp";
import family2 from "../../assets/images/first-birthday-family-balloon-party-portrait.webp";
import { ArticlePhoto } from "./ArticlePhoto";
import { UiIcon } from "./components/UiIcons";
import { GuideCtaActions } from "./components/GuideCtaActions";
import "./NewbornPages.scss";
import "./NewbornArticle.scss";
import "./FamilyArticle.scss";

const preparationTimeline = [
  {
    title: "One week before",
    text: "Confirm the location, timing, family members and the photographs that matter most to you.",
  },
  {
    title: "Two to three days before",
    text: "Try on complete outfits, remove tags and steam every piece so nothing is left for the last minute.",
  },
  {
    title: "The night before",
    text: "Pack one practical bag and keep each person’s clothing, shoes and accessories together.",
  },
  {
    title: "Photo day",
    text: "Protect meals and rest, leave extra travel time and arrive ready to enjoy one another.",
  },
];

export const HowToPrepareForFamilySession = (): ReactElement => (
  <main className="newborn-page newborn-article-page family-article-page">
    <article>
      <ArticleHero
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Resources", to: "/resources/" },
          { label: "Family", to: "/resources/family/" },
          { label: "How to Prepare for Your Family Photo Session" },
        ]}
        category="Family session guide"
        description="A complete, low-stress plan for outfits, children, timing and the little details that make photo day feel easy."
        imageAlt="Family spending relaxed time together during a photography session"
        imagePosition="center 44%"
        imageSrc={familyHeroImage}
        readingTime="8 min read"
        title="How to Prepare for Your Family Photo Session"
        updatedAt="2026-08-05"
        updatedLabel="Updated August 2026"
      />

      <div className="newborn-shell newborn-article-layout">
        <aside className="newborn-article-callout">
          <span className="newborn-article-callout__icon">
            <UiIcon name="star" />
          </span>
          <div>
            <h2>Quick answer</h2>
            <p>
              Decide what matters most, prepare complete outfits in advance, pack one small essentials bag and protect
              your family’s normal meals and rest. You do not need to practice poses or ask anyone to perform—I will
              guide the session while you focus on being together.
            </p>
          </div>
        </aside>

        <nav aria-label="In this guide" className="newborn-article-toc">
          <h2>In this guide</h2>
          <ol>
            <li>
              <a href="#timeline">Preparation timeline</a>
            </li>
            <li>
              <a href="#priorities">Choose your priorities</a>
            </li>
            <li>
              <a href="#outfits">Organize outfits</a>
            </li>
            <li>
              <a href="#pack">Pack one practical bag</a>
            </li>
            <li>
              <a href="#photo-day">Your photo-day rhythm</a>
            </li>
            <li>
              <a href="#changes">When plans change</a>
            </li>
          </ol>
        </nav>

        <section className="newborn-article-section" id="timeline">
          <p className="newborn-article-kicker">A SIMPLE COUNTDOWN</p>
          <h2>Your Family Session Preparation Timeline</h2>
          <p>A little preparation spread across the week feels much easier than making every decision on photo day.</p>
          <div className="family-ready-plan">
            {preparationTimeline.map((step) => (
              <article key={step.title}>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="newborn-article-section newborn-article-split" id="priorities">
          <div>
            <p className="newborn-article-kicker">START WITH YOUR FAMILY</p>
            <h2>Tell me what matters before we begin</h2>
            <p>
              Share the names and ages of everyone attending, your children’s personalities and any family dynamic I
              should understand. Let me know about mobility needs, sensitivities or a relative who may need more time.
            </p>
            <p>
              If there is one must-have grouping—siblings together, grandparents with grandchildren or a quiet portrait
              with your partner—mention it in advance. A short priority list is more useful than a long collection of
              poses to reproduce.
            </p>
          </div>
          <ArticlePhoto
            alt="Mother holding her baby beneath spring blossoms during a family session"
            className="newborn-article-split__photo"
            label="PHOTO: CONNECTION FIRST"
            src={family1}
          />
        </section>

        <section className="newborn-article-section" id="outfits">
          <p className="newborn-article-kicker">MAKE GETTING DRESSED EASY</p>
          <h2>Prepare complete outfits—not piles of options</h2>
          <p>
            Try on every outfit together before the session. Check the fit while sitting and moving, then place each
            person’s clothing, shoes and accessories in one group. This catches missing pieces early and makes dressing
            the family much calmer.
          </p>
          <p>
            Bring a backup for a young child when useful, but avoid carrying several competing choices. For color,
            texture and coordination ideas, use the dedicated{" "}
            <Link to="/resources/family/what-to-wear-for-family-photos/">family outfit guide</Link>.
          </p>
        </section>

        <section className="newborn-article-section" id="pack">
          <p className="newborn-article-kicker">PACK LIGHT, PACK PURPOSEFULLY</p>
          <h2>What to put in your session bag</h2>
          <ul className="newborn-article-checklist">
            <li>
              <UiIcon name="check" />
              <span>Water and a clean, familiar snack for children.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>Wipes, tissues and one practical backup outfit.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>A brush, lint roller and small touch-up essentials.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>One comfort item for a young child and a weather-appropriate layer.</span>
            </li>
          </ul>
          <aside className="newborn-article-callout newborn-article-callout--tip">
            <span className="newborn-article-callout__icon">
              <UiIcon name="tip" />
            </span>
            <div>
              <h3>Keep hands and pockets clear</h3>
              <p>Leave phones, keys and bulky bags together in one safe place once the session starts.</p>
            </div>
          </aside>
        </section>

        <section
          className="newborn-article-section newborn-article-split newborn-article-split--reverse"
          id="photo-day"
        >
          <ArticlePhoto
            alt="Family gathered at a blue and gold first birthday celebration"
            className="newborn-article-split__photo"
            label="PHOTO: AN UNRUSHED START"
            src={family2}
          />
          <div>
            <p className="newborn-article-kicker">ON THE DAY</p>
            <h2>Protect your family’s normal rhythm</h2>
            <p>
              Keep meals and naps as predictable as possible and avoid filling the hours before the session with extra
              activities. Build in more dressing and travel time than you think you need.
            </p>
            <p>
              Tell children that you are going to spend time and play together—not that they must smile perfectly. The
              full <Link to="/resources/family/how-to-prepare-kids-for-family-photos/">guide to preparing children</Link>{" "}
              has an age-friendly, low-pressure plan.
            </p>
          </div>
        </section>

        <section className="newborn-article-section" id="changes">
          <p className="newborn-article-kicker">FLEXIBILITY IS PART OF THE PLAN</p>
          <h2>What if the weather or mood changes?</h2>
          <p>
            Children may need a pause, the weather may shift and someone may arrive feeling shy. None of that ruins the
            session. We can walk, play, photograph a different grouping or take a quiet reset and return when everyone
            is ready.
          </p>
          <p>
            Your preparation gives us a comfortable starting point; it does not require the day to be perfect. The best
            images often come from letting the family in front of the camera be exactly who they are.
          </p>
        </section>

        <section className="newborn-article-cta family-article-cta">
          <div>
            <p className="newborn-article-kicker">READY TO PLAN YOUR SESSION?</p>
            <h2>Let’s make photo day feel like your family</h2>
            <p>Review the family session details and pricing, or contact me if you need help planning for your group.</p>
          </div>
          <GuideCtaActions category="family" />
        </section>
      </div>
    </article>
  </main>
);

export default HowToPrepareForFamilySession;
